import React, { ChangeEvent, type FC, type ReactNode, useEffect, useRef, useState } from 'react';

import { Icon, IconSize } from '../Icon';
import * as S from './input.styles';
import { InputProps } from './input.types';

import ClosedEye from '@/icons/closed_eye.icon.svg';
import OpenEye from '@/icons/open_eye.icon.svg';
import { Tooltip } from '../Tooltip';

const CharCount: FC<{
  current: number;
  maxLength: InputProps['maxLength'];
}> = ({ current, maxLength }) => {
  if (!maxLength) return null;
  return (
    <S.CharCount>
      <span>{current}</span>/<span>{maxLength}</span>
    </S.CharCount>
  );
};

const Wrapper: FC<{
  children: ReactNode;
  message?: ReactNode;
  useTooltip: boolean;
  visible?: boolean;
}> = ({ children, message, useTooltip, visible = false }) =>
  visible ? (
    <Tooltip
      content={message}
      delayLeaveOn={2500}
      hide={!useTooltip}
      leaveOnContent
      position="bottom"
      preserveWidth
      type="text"
    >
      {children}
    </Tooltip>
  ) : (
    <S.FullWidth>{children}</S.FullWidth>
  );

export const Input: FC<InputProps & React.HTMLProps<HTMLInputElement>> = ({
  eyePosition = 'right',
  icon: Svg,
  iconPosition = 'left',
  isDisabled,
  isHidable,
  isRequired,
  max,
  maxLength,
  min,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  regex,
  regexValidation = 'focus',
  showTooltip = false,
  textAlign,
  type,
  value,
  width,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  as, // NOTE(omer): Excluding from restProps
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ref, // NOTE(omer): Excluding from restProps
  ...restProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isHidden, setIsHidden] = useState(!!isHidable);
  const [inputValue, setInputValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const [validateRegex, setValidateRegex] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    // Handle user defined onChange
    if (onChange) {
      onChange(e);
    }
  };

  useEffect(() => {
    if (value) {
      setInputValue(String(value));
    }
  }, [value]);

  useEffect(() => {
    setIsValid(true);

    if (validateRegex) {
      if (regex && inputValue.length) {
        const { regex: regexString, externalValidation } = regex;
        const regexPattern = new RegExp(regexString || '');
        const isValidInput = externalValidation
          ? externalValidation?.valid
          : regexPattern.test(inputValue);
        setIsValid(isValidInput);
      }

      if (min && max && (inputValue < min || inputValue > max)) {
        setIsValid(false);
      }
    }
  }, [inputValue, validateRegex, regex?.externalValidation]);

  useEffect(() => {
    // show tooltip inmediately only for password type inputs
    if (isHidable) {
      if (!isValid && showTooltip && regex?.message) {
        setTooltipVisible(true);
      } else {
        setTooltipVisible(false);
      }
    }
  }, [isValid, showTooltip]);

  const handleOnFocus = (e) => {
    !isHidable && setTooltipVisible(false);
    setValidateRegex(regexValidation === 'focus');
    onFocus?.(e);
  };
  const handleOnBlur = (e) => {
    if (!isHidable && !isValid && showTooltip && regex?.message) {
      setTooltipVisible(true);
    }
    setValidateRegex(true);
    onBlur?.(e);
  };

  return (
    <S.Container $width={width}>
      <Wrapper visible={showTooltip} useTooltip={isTooltipVisible} message={regex?.message ?? null}>
        <S.InputContainer onClick={() => inputRef.current?.focus()} $isValid={isValid}>
          {Svg && iconPosition === 'left' && (
            <S.Icon>
              <Icon icon={Svg} size={IconSize.S} />
            </S.Icon>
          )}
          {isHidable && eyePosition === 'left' && (
            <S.EyeButton $isHidden={isHidden} onClick={() => setIsHidden((prev) => !prev)}>
              <Icon icon={isHidden ? ClosedEye : OpenEye} size={IconSize.S} />
            </S.EyeButton>
          )}
          <S.Input
            value={value}
            ref={inputRef}
            placeholder={placeholder}
            $isDisabled={!!isDisabled}
            $isRequired={isRequired ? isRequired.value : false}
            $isValid={isValid}
            type={isHidden ? 'password' : type}
            maxLength={maxLength}
            onChange={handleChange}
            $textAlign={textAlign}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            {...restProps}
          />
          {Svg && iconPosition === 'right' && <S.Icon>{<Svg />}</S.Icon>}
          {isHidable && eyePosition === 'right' && (
            <S.EyeButton
              $isHidden={isHidden}
              onClick={(e) => {
                e.stopPropagation();
                setIsHidden((prev) => !prev);
              }}
            >
              <Icon icon={isHidden ? ClosedEye : OpenEye} size={IconSize.S} />
            </S.EyeButton>
          )}
          <CharCount current={value?.toString().length ?? 0} maxLength={maxLength} />
        </S.InputContainer>
      </Wrapper>
    </S.Container>
  );
};
